import React, { useState, useRef, useEffect, useCallback } from "react";
import clsx from "clsx";
import SynthTooltip from "components/common/SynthTooltip";
import { firebaseService } from "services/firebaseService";
import { isFileSizeLessThanOrEqualTo } from "helpers/compareFileSizeInMb";
import toast from "react-hot-toast";
import CustomToast from "components/common/CustomToast";
import { displayFileIcon } from "helpers";
import { convertBytes } from "helpers/displayFileSize";
import SpaceMessageAvatar from "./SpaceMessageAvatar";
import useGetProductList from "hooks/inventoryHooks/useGetProductList";
import SpaceMessageLoadingState from "./SpaceMessageLoadingState";
import EmojiPicker, { EmojiClickData, EmojiStyle } from "emoji-picker-react";
import { parseMentions, extractMentions } from "utils/mentionUtils";
import type { SpaceMember, SpaceGuest } from "redux/Ldb/types/spaceTypes";

type MentionOption = {
    id: string;
    name: string;
    avatar?: string;
    type: "member" | "ai" | "guest";
};

type SpaceStyleMessageInputProps = {
    onSendMessage: (message: string, mentions?: any[], attachments?: any[], productReferences?: any[]) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    spaceMembers?: (SpaceMember | SpaceGuest)[];
    currentUserId?: string;
};

// Extract product references from message content
const extractProductReferencesFromMessage = (content: string): any[] => {
    const productReferences: any[] = [];
    const productRegex = /\/([^\/\s]+(?:\s+[^\/\s]+)*)\s+SKU:([A-Za-z0-9\-_]+)/g;
    let match;

    while ((match = productRegex.exec(content)) !== null) {
        const productName = match[1];
        const sku = match[2];
        
        productReferences.push({
            sku,
            name: productName || sku,
            type: "product",
        });
    }

    return productReferences;
};

// Extract mentions from message content and format them for the API
const extractMentionsForAPI = (content: string, spaceMembers: (SpaceMember | SpaceGuest)[] = [], currentUserId?: string): any[] => {
    const mentions: any[] = [];

    // Parse @Synth mentions
    if (content.includes("@Synth")) {
        mentions.push({
            kind: "synth",
            user: {
                ldbId: currentUserId || "",
            },
        });
    }

    // Parse @user mentions (space members and guests)
    spaceMembers.forEach((member) => {
        let memberName = "";
        let memberId = "";
        
        // Handle both team members and guests with the same logic as display
        if ("ldb" in member && member.ldb) {
            // Team member - use email if no name is available
            memberName = member.ldb?.fullName || member.ldb?.firstName || member.ldb?.email || member.ldb?.profile?.businessName || "";
            memberId = member.ldbId || member.ldb._id;
        } else if ("guest" in member && member.guest) {
            // Guest user - prioritize name, fallback to email
            memberName = `${member.guest?.firstName || ""} ${member.guest?.lastName || ""}`.trim() || 
                       member.guest?.email || "";
            memberId = member.guestId || member.guest._id;
        }
        
        // Use word boundary regex for more accurate matching
        if (memberName) {
            const escapedName = memberName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`(^|\\s)@${escapedName}(?=\\s|[.,!?;:]|$)`, 'i');
            if (regex.test(content)) {
                // Different structure for normal users vs guests
                if ("ldb" in member && member.ldb) {
                    // Normal user - use ldbId
                    mentions.push({
                        kind: "user",
                        user: {
                            ldbId: memberId,
                        },
                    });
                } else if ("guest" in member && member.guest) {
                    // Guest user - use guestId for proper task creation
                    mentions.push({
                        kind: "user",
                        user: {
                            guestId: memberId,
                        },
                    });
                }
            }
        }
    });

    return mentions;
};

export default function SpaceStyleMessageInput({
    onSendMessage,
    placeholder = "Send a message",
    disabled = false,
    className,
    spaceMembers = [],
    currentUserId,
}: SpaceStyleMessageInputProps) {
    const [message, setMessage] = useState("");
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const [filesAttached, setFilesAttached] = useState<{ fileName: string; path: string; size?: number }[]>([]);
    const [uploadingDoc, setUploadingDoc] = useState<boolean>(false);
    const [loadingAttachName, setLoadingAttachName] = useState<string>("");
    
    // Track product references for including in the payload
    const [productReferences, setProductReferences] = useState<Map<string, any>>(new Map());
    
    // Mentions functionality
    const [showMentions, setShowMentions] = useState(false);
    const [mentionQuery, setMentionQuery] = useState("");
    const [mentionOptions, setMentionOptions] = useState<MentionOption[]>([]);
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const [cursorPosition, setCursorPosition] = useState(0);
    const mentionsRef = useRef<HTMLDivElement>(null);

    // Slash command functionality for products
    const [showSlashCommands, setShowSlashCommands] = useState(false);
    const [slashQuery, setSlashQuery] = useState("");
    const [selectedSlashIndex, setSelectedSlashIndex] = useState(0);
    const [activeProductTab, setActiveProductTab] = useState<string>("Finished Goods");
    const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
    
    // Emoji picker functionality
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiRef = useRef<HTMLDivElement>(null);
    
    // Product tabs configuration (same as Flow)
    const productTabs = [
        "Finished goods",
        "Components", 
        "Work in Progress (WIP)"
    ];
    
    const productTabValues = [
        "Finished Goods",
        "Raw Material", 
        "Work-In-Progress"
    ];
    
    // Handle tab change
    const handleTabChange = (tabIndex: number) => {
        setActiveTabIndex(tabIndex);
        setActiveProductTab(productTabValues[tabIndex]);
        setProductCategory(productTabValues[tabIndex]); // Trigger hook to fetch new data
        setProductSearch(""); // Clear search when switching tabs
        setSlashQuery(""); // Clear slash query
    };
    const slashRef = useRef<HTMLDivElement>(null);
    const productRef = useRef<HTMLDivElement>(null);

    // Inject CSS to ensure cursor is always in front
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            textarea[data-space-input="true"] {
                caret-color: #374151 !important;
                position: relative !important;
                z-index: 9999 !important;
            }
            textarea[data-space-input="true"]:focus {
                caret-color: #374151 !important;
                z-index: 9999 !important;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };
    }, []);

    // Product list hook for slash commands
    const {
        data: productData,
        isFetching: productListLoading,
        handleDebouncedChange: handleProductSearch,
        setDebouncedSearch: setProductSearch,
        setProductCategory,
    } = useGetProductList(productRef.current, activeProductTab || "Finished Goods", true, true);


    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        // Only allow sending if there's text content (files require text description)
        if (message.trim() && !disabled) {
            // Convert filesAttached to attachments format
            const attachments = filesAttached.map((file) => {
                // Try to determine file type from extension
                const extension = file.fileName.split('.').pop()?.toLowerCase();
                let fileType = "application/octet-stream";
                
                if (extension) {
                    // Common file type mappings
                    const mimeTypes: Record<string, string> = {
                        'jpg': 'image/jpeg',
                        'jpeg': 'image/jpeg',
                        'png': 'image/png',
                        'gif': 'image/gif',
                        'pdf': 'application/pdf',
                        'doc': 'application/msword',
                        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'xls': 'application/vnd.ms-excel',
                        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'txt': 'text/plain',
                        'csv': 'text/csv',
                    };
                    fileType = mimeTypes[extension] || fileType;
                }
                
                return {
                    url: file.path,
                    path: file.path,
                    filename: file.fileName,
                    fileType: fileType,
                };
            });

            // Extract product references from the current message content
            const productRefs = extractProductReferencesFromMessage(message.trim());
            
            // Extract mentions from the message content
            const extractedMentions = extractMentionsForAPI(message.trim(), spaceMembers, currentUserId);

            // Use message content or empty string if only sending attachments
            const messageContent = message.trim() || "";


            onSendMessage(messageContent, extractedMentions, attachments, productRefs);
            setMessage("");
            setFilesAttached([]); // Clear attachments after sending
            setProductReferences(new Map()); // Clear product references after sending
            // Close all dropdowns
            setShowMentions(false);
            setShowSlashCommands(false);
            setShowEmojiPicker(false);
            // Reset textarea height
            if (textareaRef.current) {
                textareaRef.current.style.height = "auto";
            }
        }
    };


    // Create mention options from space members + Synth AI
    useEffect(() => {

        const memberOptions: MentionOption[] = spaceMembers
            .filter((member) => {
                // Include both team members (with ldb data) and guests (with guest data)
                const hasValidLdb = "ldb" in member && member.ldb;
                const hasValidGuest = "guest" in member && member.guest;
                const isValid = hasValidLdb || hasValidGuest;

                
                if (!isValid) {
                    return false;
                }

                // Filter out the current user - they shouldn't be able to tag themselves
                let memberId: string | undefined;
                if ("ldb" in member && member.ldb) {
                    memberId = member.ldbId || member.ldb._id;
                } else if ("guest" in member && member.guest) {
                    memberId = member.guestId || member.guest._id;
                }
                
                if (currentUserId && memberId === currentUserId) {
                    return false;
                }
                
                return true;
            })
            .map((member) => {
                // Handle both team members and guests
                if ("ldb" in member && member.ldb) {
                    // Team member - use email if no name is available
                    const memberName = member.ldb?.fullName || member.ldb?.firstName || member.ldb?.email || member.ldb?.profile?.businessName || "Team Member";
                    return {
                        id: member.ldbId || member.ldb._id,
                        name: memberName,
                        avatar: member.ldb?.profile?.avatar,
                        type: "member" as const,
                    };
                } else if ("guest" in member && member.guest) {
                    // Guest user - prioritize name, fallback to email
                    const guestName =
                        `${member.guest?.firstName || ""} ${member.guest?.lastName || ""}`.trim() ||
                        member.guest?.email ||
                        "Guest";
                    
                    return {
                        id: member.guestId || member.guest._id,
                        name: guestName,
                        avatar: undefined, // Guests don't have avatars in this system
                        type: "guest" as const,
                    };
                }
                // Fallback 
                return {
                    id: "unknown",
                    name: "User",
                    type: "member" as const,
                };
            });

        // Add Synth AI as an option at the top
        const synthOption: MentionOption = {
            id: "synth",
            name: "Synth",
            type: "ai" as const,
        };

        setMentionOptions([synthOption, ...memberOptions]);
    }, [spaceMembers, currentUserId]);

    // Close emoji picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (emojiRef.current && !emojiRef.current.contains(event.target as Node)) {
                setShowEmojiPicker(false);
            }
        };

        if (showEmojiPicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker]);

    // Auto-resize textarea based on content
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${Math.max(textareaRef.current.scrollHeight, 60)}px`;
        }
    }, [message]);

    // Create product options for slash commands
    const productOptions =
        productData?.map((product) => ({
        id: product._id,
        name: `${product.sku} - ${product.productName}`,
        sku: product.sku,
        productName: product.productName,
            type: "product",
    })) || [];

    const handleKeyDown = (e: React.KeyboardEvent) => {
        
        // Filter mention options based on current query
        const filteredOptions = mentionOptions.filter((option) =>
            option.name.toLowerCase().includes(mentionQuery.toLowerCase())
        );

        if (showMentions && filteredOptions.length > 0) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedMentionIndex((prev) =>
                    prev < filteredOptions.length - 1 ? prev + 1 : 0
                );
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedMentionIndex((prev) =>
                    prev > 0 ? prev - 1 : filteredOptions.length - 1
                );
            } else if (e.key === "Enter") {
                e.preventDefault();
                handleMentionSelect(filteredOptions[selectedMentionIndex]);
            } else if (e.key === "Escape") {
                setShowMentions(false);
                setMentionQuery("");
                setSelectedMentionIndex(0);
            }
        } 
        // Handle slash command navigation
        else if (showSlashCommands && productOptions.length > 0) {
            const filteredProducts = productOptions.filter(
                (option) =>
                option.name.toLowerCase().includes(slashQuery.toLowerCase()) ||
                option.sku.toLowerCase().includes(slashQuery.toLowerCase())
            );
            
            if (filteredProducts.length > 0) {
                if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setSelectedSlashIndex((prev) =>
                        prev < filteredProducts.length - 1 ? prev + 1 : 0
                    );
                } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setSelectedSlashIndex((prev) =>
                        prev > 0 ? prev - 1 : filteredProducts.length - 1
                    );
                } else if (e.key === "Enter") {
                    e.preventDefault();
                    handleSlashCommandSelect(filteredProducts[selectedSlashIndex]);
                } else if (e.key === "Escape") {
                    setShowSlashCommands(false);
                    setSlashQuery("");
                    setSelectedSlashIndex(0);
                }
            }
        } else if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        const cursorPos = e.target.selectionStart;
        setMessage(value);
        setCursorPosition(cursorPos);
        
        // Auto-resize textarea
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }

        // Check for @ mentions and / slash commands
        const textBeforeCursor = value.substring(0, cursorPos);
        const words = textBeforeCursor.split(/\s/);
        const lastWord = words[words.length - 1];

        if (lastWord.startsWith("@") && lastWord.length > 0) {
            const query = lastWord.substring(1).toLowerCase();
            setMentionQuery(query);
            setShowMentions(true);
            setSelectedMentionIndex(0);
            // Hide slash commands when showing mentions
            setShowSlashCommands(false);
            setSlashQuery("");
        } else if (lastWord.startsWith("/") && lastWord.length > 0) {
            const query = lastWord.substring(1).toLowerCase();
            setSlashQuery(query);
            setShowSlashCommands(true);
            setSelectedSlashIndex(0);
            // Search products based on query
            setProductSearch(query);
            // Hide mentions when showing slash commands
            setShowMentions(false);
            setMentionQuery("");
        } else {
            setShowMentions(false);
            setMentionQuery("");
            setShowSlashCommands(false);
            setSlashQuery("");
        }
    };

    const handleMentionSelect = (option: MentionOption) => {
        const textBeforeCursor = message.substring(0, cursorPosition);
        const textAfterCursor = message.substring(cursorPosition);
        const words = textBeforeCursor.split(/\s/);
        const lastWordIndex = words.length - 1;
        
        // Replace the @ mention with the selected option and add a space
        words[lastWordIndex] = `@${option.name} `;
        const newTextBeforeCursor = words.join(" ");
        const newMessage = newTextBeforeCursor + textAfterCursor;
        
        setMessage(newMessage);
        setShowMentions(false);
        setMentionQuery("");
        setSelectedMentionIndex(0);
        
        // Focus back to textarea and set cursor position after the space
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                const newCursorPos = newTextBeforeCursor.length;
                textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
                setCursorPosition(newCursorPos);
            }
        }, 0);
    };

    const handleSlashCommandSelect = (product: any) => {
        const textBeforeCursor = message.substring(0, cursorPosition);
        const textAfterCursor = message.substring(cursorPosition);
        
        // Find the last word that starts with '/' to replace
        const lastSlashIndex = textBeforeCursor.lastIndexOf("/");
        if (lastSlashIndex === -1) return; // Safety check
        
        let cleanProductName = product.name;
        if (cleanProductName.startsWith(product.sku)) {
            cleanProductName = cleanProductName
                .substring(product.sku.length)
                .replace(/^\s*-\s*/, "")
                .trim();
        }
        
        const productReference = `/${cleanProductName} SKU:${product.sku} `;
        const beforeSlash = textBeforeCursor.substring(0, lastSlashIndex);
        const newMessage = beforeSlash + productReference + textAfterCursor;

        // Store the product data for later inclusion in the payload
        setProductReferences(prev => {
            const newMap = new Map(prev);
            newMap.set(product.sku, {
                id: product._id,
                sku: product.sku,
                name: product.productName,
                fullDisplayName: product.name,
                type: "product"
            });
            return newMap;
        });
        
        // Calculate the exact cursor position after the product reference
        const newCursorPos = beforeSlash.length + productReference.length;
        
        setMessage(newMessage);
        setShowSlashCommands(false);
        setSlashQuery("");
        setSelectedSlashIndex(0);
        
        // Update cursor position state immediately
        setCursorPosition(newCursorPos);
        
        // Focus back to textarea and set cursor position right after the product reference
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
                
                // Trigger auto-resize after setting the message
                textareaRef.current.style.height = "auto";
                textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
            }
        }, 0);
    };

    const handleMentionButtonClick = () => {
        // Insert @ at cursor position and trigger mention dropdown
        const textBeforeCursor = message.substring(0, cursorPosition);
        const textAfterCursor = message.substring(cursorPosition);
        const newMessage = textBeforeCursor + "@" + textAfterCursor;
        
        setMessage(newMessage);
        
        // Focus textarea and set cursor after the @
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                const newCursorPos = textBeforeCursor.length + 1;
                textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
                setCursorPosition(newCursorPos);
                
                // Trigger mention dropdown
                setMentionQuery("");
                setShowMentions(true);
                setSelectedMentionIndex(0);
                setShowSlashCommands(false);
            }
        }, 0);
    };

    const handleEmojiButtonClick = () => {
        // Toggle emoji picker and close other dropdowns
        setShowEmojiPicker(!showEmojiPicker);
        setShowMentions(false);
        setShowSlashCommands(false);
    };

    const handleEmojiClick = (emojiData: EmojiClickData) => {
        // Insert emoji at cursor position
        const textBeforeCursor = message.substring(0, cursorPosition);
        const textAfterCursor = message.substring(cursorPosition);
        const newMessage = textBeforeCursor + emojiData.emoji + textAfterCursor;
        
        setMessage(newMessage);
        setShowEmojiPicker(false);
        
        // Focus textarea and set cursor after the emoji
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                const newCursorPos = textBeforeCursor.length + emojiData.emoji.length;
                textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
                setCursorPosition(newCursorPos);
            }
        }, 0);
    };

    const handleSlashButtonClick = () => {
        // Insert / at cursor position and trigger slash command dropdown
        const textBeforeCursor = message.substring(0, cursorPosition);
        const textAfterCursor = message.substring(cursorPosition);
        const newMessage = textBeforeCursor + "/" + textAfterCursor;
        
        setMessage(newMessage);
        
        // Focus textarea and set cursor after the /
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                const newCursorPos = textBeforeCursor.length + 1;
                textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
                setCursorPosition(newCursorPos);
                
                // Trigger slash command dropdown
                setSlashQuery("");
                setShowSlashCommands(true);
                setSelectedSlashIndex(0);
                setProductSearch(""); // Initialize product search
                setShowMentions(false);
                setShowEmojiPicker(false);
            }
        }, 0);
    };


    // File upload handler
    const handleUpload = (fileName: string, file: File) => {
        setUploadingDoc(true);
        firebaseService.uploadFile(
            `synthally/${process.env.REACT_APP_STAGE}/space-message-docs/${fileName}`,
            file,
            (url) => {
                setFilesAttached((prevFiles) => [
                    ...prevFiles,
                    {
                        fileName: fileName,
                        path: url,
                        size: file.size,
                    },
                ]);
                setUploadingDoc(false);
                setLoadingAttachName("");
            }
        );
    };

    const deleteAttachment = (idx: number) => {
        const copyData = [...filesAttached];
        copyData?.splice(idx, 1);
        setFilesAttached(copyData);
    };

    // Filter mention options based on query
    const filteredMentionOptions = mentionOptions.filter((option) =>
        option.name.toLowerCase().includes(mentionQuery.toLowerCase())
    );

    return (
        <div className={clsx(className)}>
            {/* Loading state for file upload - Flow style */}
            {loadingAttachName && uploadingDoc && (
                <div className="mb-3 max-w-[636px]">
                    <div className="mb-2 items-center px-3 py-2 rounded-[6px] space-x-3 border border-slate-100 max-w-[250px] justify-between opacity-50 inline-flex">
                        <div className="flex items-center gap-2">
                            <div className="bg-n-20 rounded-[6px] w-[25px] h-[25px] flex items-center justify-center">
                                <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full"></div>
                            </div>
                            <div>
                                <p className="text-sm font-rocGroteskMedium text-[#64748B]">
                                    {loadingAttachName.length > 25 
                                        ? loadingAttachName.substring(0, 25) + "..."
                                        : loadingAttachName
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Attached files display - Flow style */}
            {filesAttached.length > 0 && (
                <div className="mb-3 max-w-[636px]">
                    {filesAttached.map((file, idx) => (
                        <div key={idx} className="inline-flex mb-2">
                            <div className="flex space-x-1 w-full items-center">
                                <div className="flex items-center px-2.5 py-2 rounded-xl space-x-3 border border-slate-200 flex-shrink-0 w-[250px] justify-between bg-[#fff] shadow-[0px_4px_12px_-2px_rgba(212,212,212,0.03),0px_10px_15px_-4px_rgba(212,212,212,0.01),0px_8px_6px_0px_rgba(212,212,212,0.04)]">
                                    <div className="flex items-center gap-2">
                                        {displayFileIcon(
                                            file.fileName?.split(".").pop()?.toLowerCase() || "",
                                            true,
                                            "w-[25px] h-[25px]"
                                        )}
                                        <div>
                                            <p
                                                className="text-xs font-rocGroteskMedium text-gm-50 cursor-pointer"
                                                onClick={() => {
                                                    const a = document.createElement("a");
                                                    a.href = file.path;
                                                    a.target = "_blank";
                                                    document.body.appendChild(a);
                                                    a.click();
                                                    document.body.removeChild(a);
                                                }}
                                            >
                                                {file.fileName.length > 25 
                                                    ? file.fileName.substring(0, 25) + "..."
                                                    : file.fileName
                                                }
                                            </p>
                                            <p className="text-xs font-rocGroteskMedium text-slate-500">
                                                {file.size ? convertBytes(file.size) : "Unknown size"}
                                            </p>
                                        </div>
                                    </div>
                                    <i
                                        className="ri-close-fill text-gm-50 cursor-pointer"
                                        onClick={() => deleteAttachment(idx)}
                                    ></i>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Message input container with integrated icons */}
            <div className="border border-gray-200 w-full rounded-2xl bg-white shadow-sm">
                <div className="relative" id="textAreaInput">
                    {/* Input area with textarea and mention overlay */}
                    <div className="border-b border-gray-100 px-6 pt-4 pb-4">
                        <form onSubmit={handleSubmit}>
                            <div className="relative">
                                <div 
                                    ref={overlayRef}
                                    className="absolute top-0 left-0 w-full min-h-[60px] max-h-[200px] whitespace-pre-wrap break-words pointer-events-none overflow-hidden"
                                    style={{
                                        padding: "0px", 
                                        margin: "0px",
                                        border: "0px solid transparent", // Match textarea border exactly
                                        boxSizing: "border-box", // Ensure consistent box model
                                        fontSize: "16px", // Match textarea font size exactly
                                        lineHeight: "24px", // Match textarea line height exactly
                                        fontFamily: "inherit", // Use same font family
                                        letterSpacing: "normal", // Match letter spacing
                                        wordSpacing: "normal", // Match word spacing
                                        zIndex: 1, // Behind textarea
                                    }}
                                >
                                    {message ? (() => {
                                        const parsed = parseMentions(message, spaceMembers);
                                        return parsed;
                                    })() : <span>&nbsp;</span>}
                                </div>
                                
                                <textarea
                                    ref={textareaRef}
                                    data-space-input="true"
                                    value={message}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    onScroll={() => {
                                        // Sync scroll position with overlay
                                        if (overlayRef.current && textareaRef.current) {
                                            overlayRef.current.scrollTop = textareaRef.current.scrollTop;
                                            overlayRef.current.scrollLeft = textareaRef.current.scrollLeft;
                                        }
                                    }}
                                    placeholder={placeholder}
                                    disabled={disabled}
                                    rows={3}
                                    className="w-full border-0 resize-none focus:outline-none bg-transparent min-h-[60px] max-h-[200px] overflow-y-auto placeholder-gray-400 relative"
                                    style={{
                                        color: "transparent",
                                        caretColor: "#374151 !important",
                                        WebkitTextFillColor: "transparent",
                                        padding: "0px", // Match overlay padding exactly
                                        margin: "0px", // Match overlay margin exactly
                                        border: "0px solid transparent", // Match overlay border exactly
                                        boxSizing: "border-box", // Ensure consistent box model
                                        fontSize: "16px", // Match overlay font size exactly
                                        lineHeight: "24px", // Match overlay line height exactly
                                        fontFamily: "inherit", // Use same font family
                                        letterSpacing: "normal", // Match letter spacing
                                        wordSpacing: "normal", // Match word spacing
                                        zIndex: 999, // Much higher to ensure cursor is always in front
                                    }}
                                />
                            </div>
                        </form>
                    </div>

                    {showMentions && filteredMentionOptions.length > 0 && (
                        <div 
                            ref={mentionsRef}
                            className="absolute bottom-full left-4 mb-2 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-40 w-96 max-w-md"
                        >
                            {filteredMentionOptions.map((option, index) => (
                                <div
                                    key={option.id}
                                    className={clsx(
                                        "flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors",
                                        index === selectedMentionIndex 
                                            ? "bg-gray-100 text-gray-900"
                                            : "hover:bg-gray-50"
                                    )}
                                    onClick={() => handleMentionSelect(option)}
                                >
                                    {option.type === "ai" ? (
                                        <div className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden bg-g-75">
                                            <img 
                                                src="https://storage.googleapis.com/heysynth_files/synth_webapp/Group%201.svg" 
                                                alt="Synth"
                                                className="w-4 h-4"
                                            />
                                        </div>
                                    ) : (
                                        <SpaceMessageAvatar 
                                            author={{
                                                id: option.id,
                                                name: option.name,
                                                avatar: option.avatar,
                                                role: "member",
                                            }}
                                            size="sm"
                                        />
                                    )}
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-900">
                                            {option.name}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Slash commands dropdown for products */}
                    {showSlashCommands && (
                        <div 
                            ref={slashRef}
                            className="absolute bottom-full left-4 mb-2 bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-hidden z-40 w-[480px]"
                        >
                            {/* Product tabs styled like Flow */}
                            <div className="flex items-center justify-between gap-1 bg-slate-50 border-b border-slate-200 py-1.5 px-1 rounded-t-lg">
                                {productTabs.map((tab, idx) => (
                                    <span
                                        key={idx}
                                        className={`px-2 py-1 cursor-pointer text-[13px] w-full text-center rounded whitespace-nowrap ${
                                            activeTabIndex === idx
                                                ? "bg-white text-gray-900 shadow-[0px_3px_12px_-2px_rgba(108,80,6,0.06)]"
                                                : "text-slate-400 hover:text-gray-700"
                                        }`}
                                        onClick={(e) => {
                                            handleTabChange(idx);
                                            e.stopPropagation();
                                            e.preventDefault();
                                        }}
                                    >
                                        {tab}
                                    </span>
                                ))}
                            </div>
                            
                            {/* Product list */}
                            <div className="max-h-48 overflow-y-auto">
                            {productListLoading ? (
                                <SpaceMessageLoadingState 
                                    isLoading={true}
                                    message="Loading products..."
                                    className="py-4"
                                />
                            ) : (
                                <>
                                    {productOptions
                                        .filter(
                                            (option) =>
                                                option.name
                                                    .toLowerCase()
                                                    .includes(slashQuery.toLowerCase()) ||
                                                option.sku
                                                    .toLowerCase()
                                                    .includes(slashQuery.toLowerCase())
                                        )
                                        .map((product, index) => (
                                        <div
                                            key={product.id}
                                            className={clsx(
                                                "flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors",
                                                index === selectedSlashIndex 
                                                        ? "bg-gray-100 text-gray-900"
                                                    : "hover:bg-gray-50"
                                            )}
                                            onClick={() => handleSlashCommandSelect(product)}
                                        >
                                            <div className="w-6 h-6 rounded-full flex items-center justify-center bg-slate-100">
                                                <i className="ri-box-3-line text-slate-600 text-sm"></i>
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-gray-900">
                                                    {product.sku}
                                                </p>
                                                <p className="text-xs text-gray-500 truncate">
                                                    {product.productName}
                                                </p>
                                            </div>
                                            <div className="text-xs text-gray-400">
                                                /{product.sku}
                                            </div>
                                        </div>
                                    ))}
                                    {productOptions.filter(
                                        (option) =>
                                            option.name
                                                .toLowerCase()
                                                .includes(slashQuery.toLowerCase()) ||
                                            option.sku
                                                .toLowerCase()
                                                .includes(slashQuery.toLowerCase())
                                    ).length === 0 && (
                                        <div className="px-3 py-4 text-center text-sm text-gray-500">
                                            {activeTabIndex === 2 && !slashQuery.trim() 
                                                ? "No Work-in-Progress available"
                                                : slashQuery.trim()
                                                    ? `No products found for "${slashQuery}"`
                                                    : "No products available"
                                            }
                                        </div>
                                    )}
                                </>
                            )}
                            </div>
                        </div>
                    )}

                    {/* Emoji picker dropdown */}
                    {showEmojiPicker && (
                        <div 
                            ref={emojiRef}
                            className="absolute bottom-full left-4 mb-2 bg-white border border-slate-200 rounded-lg shadow-lg z-50"
                        >
                            <EmojiPicker
                                onEmojiClick={handleEmojiClick}
                                emojiStyle={EmojiStyle.NATIVE}
                                lazyLoadEmojis={true}
                                searchDisabled={false}
                                skinTonesDisabled={true}
                                previewConfig={{ showPreview: false }}
                                width={350}
                                height={400}
                            />
                        </div>
                    )}

                    {/* Hidden file input */}
                    <input
                        type="file"
                        id="spaceFileInput"
                        className="absolute z-[-1] opacity-0"
                        multiple
                        onChange={(evt) => {
                            const file = evt.target.files?.[0];
                            if (file) {
                                if (!isFileSizeLessThanOrEqualTo(file, 25)) {
                                    toast.custom((t) => (
                                        <CustomToast
                                            t={t}
                                            message="File too large, file size cannot be more than 25mb"
                                            type="error"
                                        />
                                    ));
                                    return;
                                } else {
                                    setLoadingAttachName(file.name);
                                    handleUpload(file.name, file);
                                }
                            }
                        }}
                    />

                    {/* Bottom section with icons, helper text and send button */}
                    <div className="flex items-center justify-between px-6 py-3">
                        <div className="flex items-center gap-2">
                            {/* Plus icon for file upload */}
                            <button
                                type="button"
                                onClick={() => document.getElementById("spaceFileInput")?.click()}
                                className="flex-shrink-0 w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                                title="Add file"
                            >
                                <i className="ri-add-line text-gray-600 text-sm"></i>
                            </button>

                            {/* @ mention icon */}
                            <SynthTooltip
                                divId={"mention-tooltip"}
                                position={"top"}
                                icon={
                                    <button
                                        type="button"
                                        className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center transition-colors hover:bg-gray-100"
                                        title="Mention someone"
                                        onClick={handleMentionButtonClick}
                                    >
                                        <i className="ri-at-line text-gray-600 text-sm"></i>
                                    </button>
                                }
                                content="Use @ to mention teammates, guests or Synth"
                                styling={{
                                    width: "250px",
                                    background: "#142837",
                                    borderRadius: "6px",
                                }}
                                contentClass="text-xs text-white"
                            />

                            {/* smiley/emoji icon */}
                            <SynthTooltip
                                divId={"emoji-tooltip"}
                                position={"top"}
                                icon={
                                    <button
                                        type="button"
                                        className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center transition-colors hover:bg-gray-100"
                                        title="Add emojis"
                                        onClick={handleEmojiButtonClick}
                                    >
                                        <i className="ri-emotion-line text-gray-600 text-sm"></i>
                                    </button>
                                }
                                content="Add emojis"
                                styling={{
                                    width: "200px",
                                    background: "#142837",
                                    borderRadius: "6px",
                                }}
                                contentClass="text-xs text-white"
                            />

                            <p className="text-sm text-slate-400">
                                Use / to see list of SKUs or products
                            </p>
                        </div>

                        {/* Send button */}
                        <button
                            type="button"
                            onClick={(e) => {
                                handleSubmit(e);
                            }}
                            disabled={!message.trim() || disabled}
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-400 hover:bg-amber-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                            <i className="ri-arrow-up-line text-black text-lg font-bold"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
